import type { BusinessCalendarConfig, CompanyResponse, UpdateCompanyRequest } from '@troia-v3/types';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import type { ApiResponse } from '@/lib/api';
import { Button, FormSection, Select, Switch } from '@/shared/components/ui';

import {
  buildTimeOptions,
  crossesMidnight,
  emptyDayForm,
  formatWeeklySummary,
  fromWeekForm,
  toWeekForm,
  validateWeekForm,
  WEEKDAY_LABELS,
  type BusinessDayForm,
} from './businessCalendarForm';

const START_OPTIONS = buildTimeOptions(false);
const END_OPTIONS = buildTimeOptions(true);

/** Feriados nacionais e municipais ainda não são editáveis aqui (fase 2 da
 *  spec). O que a aba PRECISA fazer é preservar o que já está gravado — ela
 *  substitui o objeto `businessCalendar` inteiro no save, e zerar
 *  `customHolidays`/`suppressedHolidayKeys` sem querer apagaria a configuração
 *  de feriados de quem já mexeu por API. O mesmo vale para `nationalHolidays.
 *  country`/`state`: a tela só edita o `enabled` (switch "Descontar feriados
 *  nacionais"); `country`/`state` são carry-over do servidor — cravar 'BR' no
 *  save reverteria uma empresa com `country: 'PT'` gravado por API (que hoje é
 *  o jeito de NÃO descontar feriados brasileiros, já que `resolve.ts` só
 *  reconhece 'BR'). */
interface HolidayCarryOver {
  nationalHolidaysCountry: string;
  nationalHolidaysState?: string;
  customHolidays: BusinessCalendarConfig['customHolidays'];
  suppressedHolidayKeys: string[];
}

const EMPTY_CARRY: HolidayCarryOver = {
  nationalHolidaysCountry: 'BR',
  customHolidays: [],
  suppressedHolidayKeys: [],
};

export const CompanyBusinessHoursTab: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const [rows, setRows] = useState<BusinessDayForm[]>(() =>
    Array.from({ length: 7 }, (_unused, weekday) => emptyDayForm(weekday)),
  );
  const [enabled, setEnabled] = useState(true);
  const [nationalHolidays, setNationalHolidays] = useState(true);
  const [carryOver, setCarryOver] = useState<HolidayCarryOver>(EMPTY_CARRY);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  // `dayBusinessMinutes` (Tarefa 9) conta 24h quando start === end de uma
  // linha — estado transitório normal enquanto o usuário troca os selects de
  // um dia (ex.: ajustando o fim antes de mexer no início). Chamar
  // `formatWeeklySummary` direto no render, a cada mudança de `rows`, faria o
  // rodapé piscar esse valor absurdo. Em vez disso, o resumo só é recalculado
  // quando a semana está válida (`validateWeekForm` null); em estado inválido
  // o rodapé mantém o último resumo bom conhecido, sem exibir o artefato.
  const [summary, setSummary] = useState<string>(() => formatWeeklySummary(rows));

  useEffect(() => {
    if (validateWeekForm(rows)) return;
    setSummary(formatWeeklySummary(rows));
  }, [rows]);

  const fetchCalendar = useCallback(async () => {
    // Hook de fetch condicionado ao `isOpen` — CLAUDE.md NUNCA #14.
    if (!isOpen) return;
    setFetching(true);
    try {
      const response = await api.get<ApiResponse<CompanyResponse>>('companies/me');
      const calendar = response.data.data?.businessCalendar;
      setRows(toWeekForm(calendar?.week));
      setEnabled(calendar?.enabled ?? true);
      setNationalHolidays(calendar?.nationalHolidays.enabled ?? true);
      setCarryOver({
        nationalHolidaysCountry: calendar?.nationalHolidays.country ?? 'BR',
        nationalHolidaysState: calendar?.nationalHolidays.state,
        customHolidays: calendar?.customHolidays ?? [],
        suppressedHolidayKeys: calendar?.suppressedHolidayKeys ?? [],
      });
    } catch {
      toast.error('Erro ao carregar o horário de atendimento');
    } finally {
      setFetching(false);
    }
  }, [isOpen]);

  useEffect(() => {
    void fetchCalendar();
  }, [fetchCalendar]);

  const patchRow = (weekday: number, patch: Partial<BusinessDayForm>): void => {
    setRows((previous) =>
      previous.map((row) => (row.weekday === weekday ? { ...row, ...patch } : row)),
    );
  };

  const handleSave = async (): Promise<void> => {
    // A validação só vale com o calendário ligado — desligado, semana vazia é
    // legítima e o backend aceita.
    if (enabled) {
      const problem = validateWeekForm(rows);
      if (problem) {
        toast.error(problem);
        return;
      }
    }

    setSaving(true);
    try {
      const payload: UpdateCompanyRequest = {
        businessCalendar: {
          enabled,
          week: fromWeekForm(rows),
          // `enabled` é o que a tela de fato edita (switch "Descontar feriados
          // nacionais"); `country`/`state` são carry-over do servidor — nunca
          // cravar 'BR' aqui (ver docstring de `HolidayCarryOver`).
          nationalHolidays: {
            enabled: nationalHolidays,
            country: carryOver.nationalHolidaysCountry,
            ...(carryOver.nationalHolidaysState !== undefined && { state: carryOver.nationalHolidaysState }),
          },
          // Preservados: esta aba ainda não edita feriados, mas substitui o
          // objeto inteiro — sem o carry-over, salvar o horário apagaria os
          // feriados próprios da empresa.
          customHolidays: carryOver.customHolidays,
          suppressedHolidayKeys: carryOver.suppressedHolidayKeys,
        },
      };

      await api.put('companies/me', payload);
      toast.success('Horário de atendimento salvo');
      await fetchCalendar();
    } catch {
      toast.error('Erro ao salvar o horário de atendimento');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#8b5cf6' }} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="company-business-hours-tab">
      <FormSection
        icon="clock"
        title="Horário de atendimento"
        description="Base do cálculo de prazo dos SLAs. O fuso é o configurado na aba Dados da Empresa."
      >
        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8fafc' }}>
          <div>
            <p id="business-hours-enabled-label" className="text-sm font-medium" style={{ color: '#1e293b' }}>Usar horário de atendimento</p>
            <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
              Desligado, os prazos passam a contar em tempo corrido, 24 horas por dia.
            </p>
          </div>
          <div data-testid="business-hours-enabled">
            <Switch
              checked={enabled}
              onChange={setEnabled}
              disabled={saving}
              aria-labelledby="business-hours-enabled-label"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-3">
          {rows.map((row) => {
            const overnight = crossesMidnight(row);
            return (
              <div key={row.weekday} className="rounded-lg border p-3" style={{ borderColor: '#e5e7eb' }} data-testid={`business-hours-day-${row.weekday}`}>
                <div className="flex items-center justify-between gap-3">
                  <span id={`business-hours-day-label-${row.weekday}`} className="text-sm font-medium" style={{ color: '#1e293b' }}>
                    {WEEKDAY_LABELS[row.weekday]}
                  </span>
                  <div data-testid={`business-hours-day-toggle-${row.weekday}`}>
                    <Switch
                      checked={row.enabled}
                      disabled={saving || !enabled}
                      onChange={(checked) => patchRow(row.weekday, { enabled: checked })}
                      aria-labelledby={`business-hours-day-label-${row.weekday}`}
                    />
                  </div>
                </div>

                {row.enabled && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Select
                      label="Início"
                      options={START_OPTIONS}
                      value={row.start}
                      disabled={saving || !enabled}
                      onChange={(value) => patchRow(row.weekday, { start: value })}
                      data-testid={`business-hours-start-${row.weekday}`}
                    />
                    <Select
                      label="Fim"
                      options={END_OPTIONS}
                      value={row.end}
                      disabled={saving || !enabled}
                      onChange={(value) => patchRow(row.weekday, { end: value })}
                      data-testid={`business-hours-end-${row.weekday}`}
                    />
                  </div>
                )}

                {row.enabled && overnight && (
                  <p className="text-xs mt-2" style={{ color: '#64748b' }}>
                    Turno que vira o dia — o expediente termina às {row.end} do dia seguinte, e não tem intervalo.
                  </p>
                )}

                {row.enabled && !overnight && (
                  <>
                    <div className="flex items-center justify-between gap-3 mt-3">
                      <span id={`business-hours-break-label-${row.weekday}`} className="text-sm" style={{ color: '#334155' }}>Intervalo</span>
                      <div data-testid={`business-hours-break-toggle-${row.weekday}`}>
                        <Switch
                          size="sm"
                          checked={row.hasBreak}
                          disabled={saving || !enabled}
                          onChange={(checked) => patchRow(row.weekday, { hasBreak: checked })}
                          aria-labelledby={`business-hours-day-label-${row.weekday} business-hours-break-label-${row.weekday}`}
                        />
                      </div>
                    </div>
                    {row.hasBreak && (
                      <div className="mt-2 grid grid-cols-2 gap-3">
                        <Select
                          label="Começa"
                          options={START_OPTIONS}
                          value={row.breakStart}
                          disabled={saving || !enabled}
                          onChange={(value) => patchRow(row.weekday, { breakStart: value })}
                          data-testid={`business-hours-break-start-${row.weekday}`}
                        />
                        <Select
                          label="Termina"
                          options={START_OPTIONS}
                          value={row.breakEnd}
                          disabled={saving || !enabled}
                          onChange={(value) => patchRow(row.weekday, { breakEnd: value })}
                          data-testid={`business-hours-break-end-${row.weekday}`}
                        />
                      </div>
                    )}
                  </>
                )}

                {row.truncated && (
                  <p className="text-xs mt-2" style={{ color: '#b45309' }}>
                    Este dia foi configurado com mais de duas faixas fora desta tela. Salvar aqui vai simplificá-lo para início, fim e um intervalo.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </FormSection>

      <FormSection
        icon="calendar"
        title="Feriados"
        description="Feriados nacionais brasileiros, incluindo os móveis, são calculados automaticamente a cada ano."
      >
        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border" style={{ borderColor: '#e5e7eb', backgroundColor: '#f8fafc' }}>
          <div>
            <p id="business-hours-national-holidays-label" className="text-sm font-medium" style={{ color: '#1e293b' }}>Descontar feriados nacionais</p>
            <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
              Em feriado o expediente do dia não conta para o prazo.
            </p>
          </div>
          <div data-testid="business-hours-national-holidays">
            <Switch
              checked={nationalHolidays}
              onChange={setNationalHolidays}
              disabled={saving || !enabled}
              aria-labelledby="business-hours-national-holidays-label"
            />
          </div>
        </div>
      </FormSection>

      <div className="flex items-center justify-between gap-4 pt-2">
        <span className="text-sm" style={{ color: '#64748b' }} data-testid="business-hours-summary">
          {enabled ? summary : 'Prazos contando em tempo corrido'}
        </span>
        <Button variant="primary" loading={saving} onClick={handleSave} data-testid="business-hours-save">
          Salvar
        </Button>
      </div>
    </div>
  );
};

CompanyBusinessHoursTab.displayName = 'CompanyBusinessHoursTab';
